import { useState } from 'react';
import type { NewsItem, NewsCategory } from '../../../mocks/news';

const categoryColors: Record<Exclude<NewsCategory, '全部'>, string> = {
  '公司公告': 'bg-[#1a2535] text-white',
  '產品資訊': 'bg-[#1a73b5] text-white',
  '產業動態': 'bg-[#0d9488] text-white',
};

const PAGE_SIZE = 6;

interface DetailModalProps {
  item: NewsItem;
  onClose: () => void;
}

function DetailModal({ item, onClose }: DetailModalProps) {
  const [month, day] = [item.date.slice(5, 7), item.date.slice(8, 10)];
  const year = item.date.slice(0, 4);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1a2535] px-6 py-4 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-0.5 whitespace-nowrap ${categoryColors[item.category]}`}>
              {item.category}
            </span>
            <span className="text-gray-400 text-xs">{year}.{month}.{day}</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white cursor-pointer w-6 h-6 flex items-center justify-center flex-shrink-0"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800 leading-snug">{item.title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="text-sm text-gray-600 leading-[2] whitespace-pre-line">
            {item.content}
          </div>
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mt-6 pt-5 border-t border-gray-100">
            {item.tags.map(tag => (
              <span key={tag} className="text-xs bg-[#f0f6ff] text-[#1a73b5] border border-[#d0e8f8] px-2 py-0.5">
                # {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
          <a
            href="/contact"
            className="inline-flex items-center gap-1.5 bg-[#1a73b5] text-white text-sm px-5 py-2 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            聯絡我們
          </a>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-600 text-sm px-5 py-2 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  items: NewsItem[];
}

export default function NewsList({ items }: Props) {
  const [active, setActive] = useState<NewsItem | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const paginated = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (items.length === 0) {
    return (
      <div className="py-20 text-center text-gray-400">
        <i className="ri-newspaper-line text-5xl mb-3 block" />
        <p className="text-sm">此分類目前暫無消息</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col divide-y divide-gray-100">
        {paginated.map(item => {
          const year = item.date.slice(0, 4);
          const month = item.date.slice(5, 7);
          const day = item.date.slice(8, 10);

          return (
            <article
              key={item.id}
              className="group flex flex-col sm:flex-row gap-5 py-6 cursor-pointer hover:bg-[#f9fbff] transition-colors px-2 -mx-2"
              onClick={() => setActive(item)}
            >
              {/* Date Block */}
              <div className="flex-shrink-0 flex sm:flex-col items-center sm:items-center gap-2 sm:gap-0 sm:w-16 sm:pt-1">
                <div className="bg-[#1a73b5] text-white text-xl font-bold w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                  {day}
                </div>
                <div className="text-xs text-gray-400 sm:text-center mt-0 sm:mt-1.5">
                  <span>{year}.{month}</span>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 whitespace-nowrap ${categoryColors[item.category]}`}>
                    {item.category}
                  </span>
                  {item.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="text-xs bg-[#f0f6ff] text-[#1a73b5] px-1.5 py-0.5 hidden sm:inline">
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="text-base font-bold text-gray-800 group-hover:text-[#1a73b5] transition-colors mb-1.5 leading-snug">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500 leading-[1.8] line-clamp-2">
                  {item.excerpt}
                </p>
                <div className="flex items-center gap-1 mt-3 text-xs text-[#1a73b5] font-medium">
                  <span>閱讀全文</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-10 pt-8 border-t border-gray-100">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-9 h-9 flex items-center justify-center border border-gray-200 text-gray-500 hover:border-[#1a73b5] hover:text-[#1a73b5] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`w-9 h-9 flex items-center justify-center text-sm cursor-pointer transition-colors border
                ${page === n
                  ? 'bg-[#1a73b5] text-white border-[#1a73b5]'
                  : 'border-gray-200 text-gray-600 hover:border-[#1a73b5] hover:text-[#1a73b5]'}`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 flex items-center justify-center border border-gray-200 text-gray-500 hover:border-[#1a73b5] hover:text-[#1a73b5] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {active && <DetailModal item={active} onClose={() => setActive(null)} />}
    </>
  );
}
